/** Deling (share.mo2.dk): large files are uploaded straight to Deling and sent as a link. */

export interface DelingConfig {
  ticket: string;
  shareUrl: string;
  linkDays: number;
  autoMb: number;
}

export interface DelingResult {
  url: string;
  name: string;
  size: number;
  expires: number; // ms
  fileId: string;
  shareId: string;
}

let cached: { cfg: DelingConfig; at: number } | null = null;

/** Upload ticket from the BFF; reused until ~8 min old (tickets live 10 min). */
export async function getDelingConfig(csrfToken: string, fresh = false): Promise<DelingConfig> {
  if (!fresh && cached && Date.now() - cached.at < 8 * 60_000) return cached.cfg;
  const res = await fetch('/api/share/ticket', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'x-zwf-csrf': csrfToken },
  });
  if (!res.ok) throw new Error(`Deling unavailable (${res.status})`);
  const cfg = (await res.json()) as DelingConfig;
  cached = { cfg, at: Date.now() };
  return cfg;
}

/** XHR upload (for progress) of the raw file body to Deling. */
export function uploadToDeling(
  file: File,
  cfg: DelingConfig,
  onProgress: (pct: number) => void,
): { promise: Promise<DelingResult>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<DelingResult>((resolve, reject) => {
    const q = `name=${encodeURIComponent(file.name)}&size=${file.size}`;
    xhr.open('POST', `${cfg.shareUrl}/api/webmail/upload?${q}`);
    xhr.setRequestHeader('Authorization', `Webmail ${cfg.ticket}`);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body: { error?: string } & Partial<DelingResult> = {};
      try {
        body = JSON.parse(xhr.responseText || '{}');
      } catch {
        /* non-JSON */
      }
      if (xhr.status === 201 && body.url?.startsWith(`${cfg.shareUrl}/`) && body.shareId) resolve(body as DelingResult);
      else reject(new Error(body.error || `Deling upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Could not reach Deling'));
    xhr.onabort = () => reject(new Error('aborted'));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

/** Revoke the link (the file stays in the user's "Webmail" folder on Deling). */
export async function revokeDelingLink(shareId: string, csrfToken: string): Promise<void> {
  const cfg = await getDelingConfig(csrfToken);
  await fetch(`${cfg.shareUrl}/api/webmail/link/${encodeURIComponent(shareId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Webmail ${cfg.ticket}` },
  });
}

const MONTHS = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];

/** "15. okt. 2026" */
export function formatDanishDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()}. ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "148 MB", "2,4 MB", "820 KB" */
export function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${(mb >= 10 ? Math.round(mb).toString() : mb.toFixed(1).replace('.', ','))} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** The block inserted into the mail body (Danish — the recipient reads it). */
export function delingBlockHtml(r: DelingResult): string {
  return (
    `📎 <b>${esc(r.name)}</b> (${formatSize(r.size)}) — ` +
    `<a href="${esc(r.url)}">hent filen</a> · gyldigt til ${formatDanishDate(r.expires)}`
  );
}
